import { Box, Button } from "@mui/material"
import { Plus } from "lucide-react"
import { useState, type JSX } from "react"
import { useNavigate } from "react-router-dom"

import { useCan } from "@/auth/permissions"
import { PageHeader } from "@/components/ui/page-header"
import { withCustomersAccess } from "@crm-src/components/page-permission-guard"
import { NewPreShipDialog } from "@crm-src/components/pre-ship/new-pre-ship-dialog"
import { PreShipList } from "@crm-src/components/pre-ship/pre-ship-list"

/**
 * CRM ▸ Customers ▸ Pre-ship Questionnaires (`/crm/pre-ship`, 2026-10-03).
 *
 * Every pre-ship questionnaire on a customer the viewer can see — the forms a
 * customer fills in before XOP ships an appliance. The same list sits on each
 * customer profile's Pre-ship tab, narrowed to that customer. Gated like the
 * customer it belongs to: the Customers page to read, `crm:edit` to create.
 * See docs/crm.md → "Pre-ship questionnaires".
 */
function PreShipQuestionnairesPage(): JSX.Element {
  const navigate = useNavigate()
  const canEdit = useCan("crm:edit")
  const [open, setOpen] = useState(false)

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }} data-cy="preship-page">
      <PageHeader
        title="Pre-ship Questionnaires"
        description="What each customer told us about their site before we ship — network, rack and power, phone lines"
        actions={
          canEdit ? (
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setOpen(true)} data-cy="preship-new-open">
              New questionnaire
            </Button>
          ) : undefined
        }
      />

      <PreShipList emptyMessage="No pre-ship questionnaires yet — start one from a customer, or with New questionnaire." />

      {canEdit && (
        <NewPreShipDialog
          open={open}
          onClose={() => setOpen(false)}
          onCreated={(q) => {
            setOpen(false)
            navigate(`/crm/pre-ship/${q.id}`)
          }}
        />
      )}
    </Box>
  )
}

export default withCustomersAccess(PreShipQuestionnairesPage)
